/**
 * Utility functions for safe base64 encoding with Unicode support
 */

/**
 * Safely encode HTML string to base64 data URL with proper UTF-8 handling
 * Handles Unicode characters that would cause btoa() to fail
 * 
 * @param html - HTML string to encode
 * @returns base64 data URL for iframe src
 */
export function encodeHtmlToBase64DataUrl(html: string): string {
  try {
    // Primary method: Use TextEncoder for proper UTF-8 encoding
    const encoder = new TextEncoder();
    const utf8Array = encoder.encode(html);
    
    // Convert Uint8Array to binary string for btoa
    let binaryString = '';
    for (const byte of utf8Array) {
      binaryString += String.fromCodePoint(byte);
    }
    
    const base64Content = btoa(binaryString);
    return `data:text/html;charset=utf-8;base64,${base64Content}`;
    
  } catch (encodingError) {
    // Fallback method for older browsers or encoding issues
    console.warn('TextEncoder failed, using fallback method:', encodingError);
    
    try {
      // Escape Unicode characters and convert
      const escaped = encodeURIComponent(html);
      // eslint-disable-next-line unicorn/prefer-string-replace-all
      const binary = escaped.replace(/%([0-9A-F]{2})/g, (_: string, hex: string) => {
        return String.fromCodePoint(Number.parseInt(hex, 16));
      });
      
      const base64Content = btoa(binary);
      return `data:text/html;charset=utf-8;base64,${base64Content}`;
      
    } catch (fallbackError) {
      console.error('Both encoding methods failed:', fallbackError);
      throw new Error('Failed to encode HTML to base64: Unicode encoding not supported');
    }
  }
}

/**
 * Test if a string contains characters outside Latin1 range
 * Useful to determine if special encoding is needed
 * 
 * @param str - String to test
 * @returns true if string contains non-Latin1 characters
 */
export function containsNonLatin1Characters(str: string): boolean {
  // Latin1 range is 0-255 (0x00-0xFF)
  // Check if string contains characters outside Latin1 range
  for (const char of str) {
    const codePoint = char.codePointAt(0);
    if (codePoint && codePoint > 255) {
      return true;
    }
  }
  return false;
}

/**
 * Inject parameters into HTML content as script tags
 * 
 * @param html - Original HTML content
 * @param params - Parameters to inject
 * @returns Modified HTML with injected parameters
 */
export function injectParametersIntoHtml(html: string, params: Record<string, string>): string {
  // Create a script tag with the parameters
  const paramsScript = `
    <script>
      window.IFRAME_PARAMS = ${JSON.stringify(params)};
      
      // Make parameters available via URL search params as well
      window.IFRAME_SEARCH_PARAMS = new URLSearchParams(${JSON.stringify(params)});
      
      // Send ready message to parent when DOM is loaded
      document.addEventListener('DOMContentLoaded', function() {
        if (window.parent !== window) {
          window.parent.postMessage({
            type: 'iframeReady',
            params: window.IFRAME_PARAMS
          }, '*');
        }
      });
    </script>
  `;
  
  // Insert the script before closing head tag, or at the beginning of body if no head
  if (html.includes('</head>')) {
    return html.replace('</head>', `${paramsScript}</head>`);
  } else if (html.includes('<body>')) {
    return html.replace('<body>', `<body>${paramsScript}`);
  } else {
    return `${paramsScript}${html}`;
  }
}

/**
 * Validate base64 data URL
 * 
 * @param dataUrl - Data URL to validate
 * @returns true if valid base64 data URL
 */
export function isValidBase64DataUrl(dataUrl: string): boolean {
  const dataUrlPattern = /^data:text\/html;charset=utf-8;base64,([A-Za-z0-9+/]*={0,2})$/;
  return dataUrlPattern.test(dataUrl);
}

/**
 * Get size of base64 encoded content in bytes
 * 
 * @param base64String - Base64 string (without data URL prefix)
 * @returns size in bytes
 */
export function getBase64Size(base64String: string): number {
  // Remove padding characters and calculate size
  // eslint-disable-next-line unicorn/prefer-string-replace-all
  const withoutPadding = base64String.replace(/=/g, '');
  return Math.floor((withoutPadding.length * 3) / 4);
}

/**
 * Format bytes to human readable string
 * 
 * @param bytes - Number of bytes
 * @returns formatted string (e.g., "1.2 KB")
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}